import React, { useState, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import { FocusRing } from '@momentum-ui/react-collaboration';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';

const FEEDBACK_CATEGORIES = [
  'Harmful',
  'Offensive',
  'Incorrect',
  'Irrelevant',
  'Too long',
  'Too short',
] as const;

type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

const MAX_CHARS = 500;
const LEARN_MORE_URL =
  'https://help.webex.com/en-us/article/n3v5r5bb/Learn-how-your-feedback-helps-us-improve';

export interface IDislikeFeedbackResult {
  categories: FeedbackCategory[];
  additionalFeedback: string;
}

interface IDislikeFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (result: IDislikeFeedbackResult) => void;
  usePortal?: boolean;
}

const ACCENT = 'var(--mds-color-theme-text-accent-normal, #1170CF)';

const S = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.5)',
  },
  dialog: {
    borderRadius: 12,
    padding: '24px',
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.8125rem',
    margin: 0,
    paddingBottom: 4,
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  pill: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: '6px 16px',
    borderRadius: 20,
    background: 'transparent',
    cursor: 'pointer',
    lineHeight: '18px',
    transition: 'border-color 0.15s ease',
  },
  pillSelected: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: '6px 16px',
    border: `2px solid ${ACCENT}`,
    borderRadius: 20,
    background: 'transparent',
    cursor: 'pointer',
    lineHeight: '18px',
  },
  label: {
    fontSize: '0.8125rem',
    margin: 0,
  },
  textareaWrap: {
    position: 'relative' as const,
  },
  textarea: {
    width: '100%',
    minHeight: 100,
    padding: '10px 12px',
    paddingBottom: 28,
    fontSize: '0.8125rem',
    background: 'transparent',
    borderRadius: 8,
    outline: 'none',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    lineHeight: '1.5',
  },
  charCount: {
    position: 'absolute' as const,
    bottom: 8,
    right: 12,
    fontSize: '0.6875rem',
    pointerEvents: 'none' as const,
  },
  link: {
    fontSize: '0.8125rem',
    color: ACCENT,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  btnClose: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: '8px 20px',
    borderRadius: 20,
    background: 'transparent',
    cursor: 'pointer',
    lineHeight: '18px',
  },
  btnSend: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: '8px 20px',
    border: 'none',
    borderRadius: 20,
    background: 'var(--mds-color-theme-button-secondary-normal, #555)',
    cursor: 'pointer',
    lineHeight: '18px',
  },
  btnSendDisabled: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: '8px 20px',
    border: 'none',
    borderRadius: 20,
    background: 'var(--mds-color-theme-button-secondary-normal, #555)',
    cursor: 'default',
    lineHeight: '18px',
    opacity: 0.5,
  },
};

export const DislikeFeedbackDialog: React.FC<IDislikeFeedbackDialogProps> = ({
  isOpen,
  onClose,
  onSend,
  usePortal = true,
}) => {
  const { t } = useTranslation('WebexCallHistory');
  const { colors, theme } = useTheme();
  const [selected, setSelected] = useState<FeedbackCategory | null>(null);
  const [text, setText] = useState('');
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const categoryButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const learnMoreRef = useRef<HTMLAnchorElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const sendButtonRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const subtitleId = useId();
  const categoryGroupId = useId();
  const feedbackFieldId = useId();
  const feedbackCountId = useId();
  const feedbackCategoryLabels: Record<FeedbackCategory, string> = {
    Harmful: t('aiSummary.feedbackCategory.harmful', { defaultValue: 'Harmful' }),
    Offensive: t('aiSummary.feedbackCategory.offensive', { defaultValue: 'Offensive' }),
    Incorrect: t('aiSummary.feedbackCategory.incorrect', { defaultValue: 'Incorrect' }),
    Irrelevant: t('aiSummary.feedbackCategory.irrelevant', { defaultValue: 'Irrelevant' }),
    'Too long': t('aiSummary.feedbackCategory.tooLong', { defaultValue: 'Too long' }),
    'Too short': t('aiSummary.feedbackCategory.tooShort', { defaultValue: 'Too short' }),
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setText('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const timer = window.setTimeout(() => categoryButtonRefs.current[0]?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', handleKey);
      window.clearTimeout(timer);
      const previousFocus = previousFocusRef.current;
      if (previousFocus && document.contains(previousFocus)) {
        previousFocus.focus();
      }
    };
  }, [isOpen, onClose]);

  const selectCategory = useCallback((cat: FeedbackCategory) => {
    setSelected(cat);
  }, []);

  const focusCategoryByIndex = useCallback((index: number) => {
    const target = categoryButtonRefs.current[index];
    if (target) {
      target.focus();
    }
  }, []);

  const handleCategoryKeyDown = useCallback((index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = (index + 1) % FEEDBACK_CATEGORIES.length;
        setSelected(FEEDBACK_CATEGORIES[nextIndex]);
        focusCategoryByIndex(nextIndex);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        event.preventDefault();
        const previousIndex = (index - 1 + FEEDBACK_CATEGORIES.length) % FEEDBACK_CATEGORIES.length;
        setSelected(FEEDBACK_CATEGORIES[previousIndex]);
        focusCategoryByIndex(previousIndex);
        break;
      }
      case 'Home':
        event.preventDefault();
        setSelected(FEEDBACK_CATEGORIES[0]);
        focusCategoryByIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setSelected(FEEDBACK_CATEGORIES[FEEDBACK_CATEGORIES.length - 1]);
        focusCategoryByIndex(FEEDBACK_CATEGORIES.length - 1);
        break;
      default:
        break;
    }
  }, [focusCategoryByIndex]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS) setText(val);
  }, []);

  const canSend = selected !== null || text.trim().length > 0;

  const handleSend = useCallback(() => {
    if (!canSend) return;
    onSend({
      categories: selected ? [selected] : [],
      additionalFeedback: text.trim(),
    });
    onClose();
  }, [canSend, selected, text, onSend, onClose]);

  const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const getFocusableElements = useCallback(() => {
    const focusedCategoryButton = categoryButtonRefs.current.find((button) => button === document.activeElement);
    const activeCategoryButton =
      focusedCategoryButton || categoryButtonRefs.current.find((button) => button?.tabIndex === 0) || categoryButtonRefs.current[0];
    const elements: Array<HTMLElement | null | undefined> = [
      activeCategoryButton,
      textareaRef.current,
      learnMoreRef.current,
      closeButtonRef.current,
      canSend ? sendButtonRef.current : null,
    ];

    return elements.filter((element): element is HTMLElement => Boolean(element));
  }, [canSend]);

  const focusAdjacentElement = useCallback((shiftKey: boolean) => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return false;

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    if (shiftKey) {
      nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === -1 || currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
    }

    focusableElements[nextIndex]?.focus();
    return true;
  }, [getFocusableElements]);

  const handleDialogKeyDownCapture = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;

    const didMoveFocus = focusAdjacentElement(event.shiftKey);
    if (!didMoveFocus) return;

    event.preventDefault();
    event.stopPropagation();
  }, [focusAdjacentElement]);

  const handleTextAreaKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Tab') return;

    const didMoveFocus = focusAdjacentElement(event.shiftKey);
    if (!didMoveFocus) return;

    event.preventDefault();
    event.stopPropagation();
  }, [focusAdjacentElement]);

  if (!isOpen) return null;

  const themed = {
    dialog: {
      ...S.dialog,
      background: colors.backgroundPrimary,
      border: `1px solid ${colors.outlineSecondary}`,
      boxShadow: theme === 'darkWebex'
        ? '0 8px 24px rgba(0,0,0,0.55)'
        : '0 8px 24px rgba(0,0,0,0.22)',
    },
    title: {
      ...S.title,
      color: colors.textPrimary,
    },
    subtitle: {
      ...S.subtitle,
      color: colors.textSecondary,
      borderBottom: `1px solid ${colors.outlineSecondary}`,
    },
    pill: {
      ...S.pill,
      color: colors.textPrimary,
      border: `1px solid ${colors.outlineSecondary}`,
    },
    pillSelected: {
      ...S.pillSelected,
      color: colors.textPrimary,
    },
    label: {
      ...S.label,
      color: colors.textSecondary,
    },
    textarea: {
      ...S.textarea,
      color: colors.textPrimary,
      border: `1px solid ${colors.outlineSecondary}`,
    },
    charCount: {
      ...S.charCount,
      color: colors.textSecondary,
    },
    btnClose: {
      ...S.btnClose,
      color: colors.textPrimary,
      border: `1px solid ${colors.outlineSecondary}`,
    },
    btnSend: {
      ...S.btnSend,
      color: colors.textPrimary,
      background: colors.backgroundSecondaryActive,
    },
    btnSendDisabled: {
      ...S.btnSendDisabled,
      color: colors.textSecondary,
      background: colors.backgroundSecondaryActive,
    },
  };

  const dialog = (
    <FocusLock returnFocus={false} autoFocus={false}>
      <div
        style={S.overlay}
        onMouseDown={handleOverlayMouseDown}
        onClick={(e) => e.stopPropagation()}
        data-testid="dislike-feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitleId}
      >
        <div
          ref={dialogRef}
          style={themed.dialog}
          onKeyDownCapture={handleDialogKeyDownCapture}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id={titleId} style={themed.title}>
            {t('aiSummary.feedbackTitle', { defaultValue: 'Your feedback helps us improve' })}
          </h2>
          <p id={subtitleId} style={themed.subtitle}>
            {t('aiSummary.feedbackSubtitle', {
              defaultValue: 'Share your thoughts on the generated content. Select the option that best applies or add your own feedback below.',
            })}
          </p>

          <p id={categoryGroupId} style={themed.label}>
            {t('aiSummary.feedbackCategoryLabel', { defaultValue: 'Feedback category' })}
          </p>
          <div style={S.pills} role="radiogroup" aria-labelledby={categoryGroupId}>
            {FEEDBACK_CATEGORIES.map((cat, index) => (
              <FocusRing key={cat}>
                <button
                  ref={(element) => {
                    categoryButtonRefs.current[index] = element;
                  }}
                  type="button"
                  style={selected === cat ? themed.pillSelected : themed.pill}
                  onClick={() => selectCategory(cat)}
                  onKeyDown={handleCategoryKeyDown(index)}
                  role="radio"
                  aria-checked={selected === cat}
                  tabIndex={selected ? (selected === cat ? 0 : -1) : index === 0 ? 0 : -1}
                >
                  {feedbackCategoryLabels[cat]}
                </button>
              </FocusRing>
            ))}
          </div>

          <label htmlFor={feedbackFieldId} style={themed.label}>
            {t('aiSummary.additionalFeedback', { defaultValue: 'Additional feedback' })}
          </label>
          <div style={S.textareaWrap}>
            <FocusRing>
              <textarea
                ref={textareaRef}
                id={feedbackFieldId}
                style={themed.textarea}
                placeholder={t('aiSummary.additionalFeedbackPlaceholder', {
                  defaultValue: 'Anything else? How could the response be improved?',
                })}
                value={text}
                onChange={handleTextChange}
                onKeyDown={handleTextAreaKeyDown}
                maxLength={MAX_CHARS}
                aria-label={t('aiSummary.additionalFeedback', { defaultValue: 'Additional feedback' })}
                aria-describedby={feedbackCountId}
              />
            </FocusRing>
            <span id={feedbackCountId} style={themed.charCount}>
              {text.length}/{MAX_CHARS}
            </span>
          </div>

          <a
            ref={learnMoreRef}
            style={S.link}
            href={LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('aiSummary.feedbackLearnMore', { defaultValue: 'Learn how your feedback helps us improve' })}
          </a>

          <div style={S.footer}>
            <button
              ref={closeButtonRef}
              type="button"
              style={themed.btnClose}
              onClick={onClose}
            >
              {t('aiSummary.close', { defaultValue: 'Close' })}
            </button>
            <button
              ref={sendButtonRef}
              type="button"
              style={canSend ? themed.btnSend : themed.btnSendDisabled}
              onClick={handleSend}
              disabled={!canSend}
            >
              {t('aiSummary.send', { defaultValue: 'Send' })}
            </button>
          </div>
        </div>
      </div>
    </FocusLock>
  );

  if (!usePortal) return dialog;
  if (typeof document === 'undefined') return null;

  return createPortal(dialog, document.body);
};
